import { IAction, IState } from './useRcsTemplateEditorContext';

export function RcsTemplateEditorReducer(state: IState, action: IAction): IState {
  const { payload } = action;

  switch (action.type) {
    case 'SET_ACTIVE_TAB':
      if (payload?.tab === undefined) {
        return state;
      }
      return {
        ...state,
        activeTab: payload.tab,
      };
    case 'SET_EDITOR_SETTINGS':
      return {
        ...state,
        editorSettings: {
          ...state.editorSettings,
          ...(payload?.theme !== undefined && { theme: payload.theme }),
          ...(payload?.readOnly !== undefined && { readOnly: payload.readOnly }),
        },
      };
    case 'SET_TEMPLATE_VERSION': {
      const versionId = payload?.versionId ?? '';
      return {
        ...state,
        previewingVersion: versionId,
      };
    }
    case 'SET_RIGHT_WINDOW_PERCENTAGE': {
      const percentage = payload?.percentage;
      const isValid =
        typeof percentage === 'number' && !Number.isNaN(percentage) && percentage >= 0 && percentage <= 100;
      if (!isValid) {
        return state;
      }
      return {
        ...state,
        rightWindowPercentage: percentage,
      };
    }
    case 'SET_UNSAVED_CHANGES':
      if (payload?.hasUnsavedChanges === undefined) {
        return state;
      }
      return {
        ...state,
        hasUnsavedChanges: payload.hasUnsavedChanges,
      };
    case 'SET_SELECTED_LANGUAGE':
      if (payload?.selectedLanguage === undefined) {
        return state;
      }
      return {
        ...state,
        selectedLanguage: payload.selectedLanguage,
      };
    case 'SET_TEMPLATE_METADATA':
      return {
        ...state,
        ...(payload?.templateName !== undefined && { templateName: payload.templateName }),
        ...(payload?.templateType !== undefined && { templateType: payload.templateType }),
        ...(payload?.supportedLanguages !== undefined && { supportedLanguages: payload.supportedLanguages }),
        ...(payload?.selectedLanguage !== undefined && { selectedLanguage: payload.selectedLanguage }),
      };
    case 'SET_RCS_CONFIG': {
      const { hasUnsavedChanges, ...rcsPayload } = payload ?? {};
      const nextRcsConfig = {
        ...state.rcsConfig,
        ...(rcsPayload.actions !== undefined && { actions: rcsPayload.actions }),
        ...(rcsPayload.imageUrl !== undefined && { imageUrl: rcsPayload.imageUrl }),
        ...(rcsPayload.cardTitle !== undefined && { cardTitle: rcsPayload.cardTitle }),
        ...(rcsPayload.description !== undefined && { description: rcsPayload.description }),
        ...(rcsPayload.smsFallbackEnabled !== undefined && {
          smsFallbackEnabled: rcsPayload.smsFallbackEnabled,
        }),
        ...(rcsPayload.smsFallbackTemplateId !== undefined && {
          smsFallbackTemplateId: rcsPayload.smsFallbackTemplateId?.trim() || undefined,
        }),
        ...(rcsPayload.smsFallbackTemplateName !== undefined && {
          smsFallbackTemplateName: rcsPayload.smsFallbackTemplateName?.trim() || undefined,
        }),
        ...(rcsPayload.smsFallbackContent !== undefined && { smsFallbackContent: rcsPayload.smsFallbackContent }),
      };
      return {
        ...state,
        rcsConfig: nextRcsConfig,
        hasUnsavedChanges: hasUnsavedChanges !== undefined ? hasUnsavedChanges : true,
        validationErrors: {},
      };
    }
    case 'SET_RCS_VALIDATION_ERRORS': {
      const errors = payload?.validationErrors;
      if (errors === undefined) {
        return state;
      }
      return {
        ...state,
        validationErrors: errors,
      };
    }
    case 'DISCARD_CHANGES': {
      const saved = state.lastSavedRcsConfig;
      return {
        ...state,
        rcsConfig: {
          ...saved,
          actions: saved.actions.map(a => ({ ...a })),
        },
        validationErrors: {},
        hasUnsavedChanges: false,
      };
    }
    case 'SET_LAST_SAVED_RCS_CONFIG': {
      const config = payload?.rcsConfig;
      if (config === undefined) {
        return state;
      }
      return {
        ...state,
        lastSavedRcsConfig: {
          ...config,
          actions: config.actions.map(a => ({ ...a })),
        },
      };
    }
    case 'UPDATE_PREVIEW_INITIAL': {
      const { compiledTemplate, lastCompiledRcsPayload, lastCompiledCompilationContext } = payload ?? {};
      if (
        compiledTemplate === undefined ||
        lastCompiledRcsPayload === undefined ||
        lastCompiledCompilationContext === undefined
      ) {
        return state;
      }
      return {
        ...state,
        compiledTemplate,
        lastCompiledRcsPayload,
        lastCompiledCompilationContext,
        requiresPreview: false,
      };
    }
    case 'SET_SNAPSHOT_JSON': {
      const snapshotJson = payload?.snapshotJson;
      if (snapshotJson === undefined) {
        return state;
      }
      return {
        ...state,
        snapshotJson,
      };
    }
    case 'SET_PREVIEW_REQUIRED': {
      const requiresPreview = payload?.requiresPreview;
      if (requiresPreview === undefined) {
        return state;
      }
      return {
        ...state,
        requiresPreview,
      };
    }
    case 'SET_SKIP_RANDOMISE_SNAPSHOT_MODAL': {
      if (payload?.skipRandomiseSnapshotModal === undefined) {
        return state;
      }
      return {
        ...state,
        skipRandomiseSnapshotModal: payload.skipRandomiseSnapshotModal,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}
